// panels/HeatmapPanel.jsx
import React, { useState } from 'react';
import { PanelWrapper } from './PanelWrapper';

const SOURCE_COLORS = {
  GCP:      '#4285f4',
  AZURE:    '#0078d4',
  MONGODB:  '#4ade80',
  DATABASE: '#a78bfa',
};

function heatColor(value, max) {
  if (value === 0 || max === 0) return 'rgba(255,255,255,0.03)';
  const intensity = value / max;
  if (intensity < 0.25) return `rgba(34,197,94,${0.15 + intensity * 0.4})`;
  if (intensity < 0.6)  return `rgba(245,158,11,${0.2 + intensity * 0.5})`;
  return `rgba(239,68,68,${0.25 + intensity * 0.65})`;
}

export function HeatmapPanel({ data, loading, title = 'Error Heatmap', subtitle = 'Errors per source × time' }) {
  const [tooltip, setTooltip] = useState(null);

  if (!data?.rows?.length || !data?.cols?.length) {
    return (
      <PanelWrapper title={title} subtitle={subtitle} datasource="MongoDB" loading={loading}>
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
          No heatmap data available
        </div>
      </PanelWrapper>
    );
  }

  const { rows, cols, values } = data;
  const maxVal = Math.max(...values.flat(), 1);

  return (
    <PanelWrapper title={title} subtitle={subtitle} datasource="MongoDB" loading={loading}>
      <div style={{ padding: '12px 14px', overflowX: 'auto', position: 'relative' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `72px repeat(${cols.length}, 1fr)`,
            gap: 2,
            minWidth: 480,
          }}
        >
          {/* Header: source label col + time cols */}
          <div style={{ fontSize: 9, color: 'var(--text-3)', padding: '2px 4px', alignSelf: 'end' }}>SOURCE</div>
          {cols.map((col, ci) => (
            <div key={ci} style={{
              fontSize: 9, color: 'var(--text-3)', textAlign: 'center',
              padding: '2px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {col}
            </div>
          ))}

          {/* Data rows */}
          {rows.map((row, ri) => (
            <React.Fragment key={ri}>
              {/* Row label */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 4,
                fontSize: 10, fontWeight: 700,
                color: SOURCE_COLORS[row] || 'var(--text-2)',
                paddingRight: 8,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: SOURCE_COLORS[row] || '#64748b',
                  flexShrink: 0,
                }} />
                {row}
              </div>

              {/* Cells */}
              {cols.map((col, ci) => {
                const v = values[ri]?.[ci] ?? 0;
                return (
                  <div
                    key={ci}
                    style={{
                      height: 22,
                      borderRadius: 3,
                      background: heatColor(v, maxVal),
                      cursor: 'default',
                      transition: 'opacity .15s',
                      border: tooltip?.ri === ri && tooltip?.ci === ci
                        ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
                    }}
                    onMouseEnter={() => setTooltip({ ri, ci, row, col, v })}
                    onMouseLeave={() => setTooltip(null)}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>

        {/* Tooltip */}
        {tooltip && (
          <div style={{
            position: 'fixed', zIndex: 999,
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '6px 10px', fontSize: 11,
            pointerEvents: 'none', transform: 'translate(12px, -50%)',
          }}>
            <div style={{ fontWeight: 600, color: SOURCE_COLORS[tooltip.row] || 'var(--text)' }}>{tooltip.row}</div>
            <div style={{ color: 'var(--text-3)' }}>{tooltip.col}</div>
            <div style={{ color: tooltip.v > 0 ? '#ef4444' : '#22c55e', marginTop: 2 }}>
              {tooltip.v} error{tooltip.v !== 1 ? 's' : ''}
            </div>
          </div>
        )}

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 10, color: 'var(--text-3)' }}>
          <span>Low</span>
          {['rgba(34,197,94,0.3)', 'rgba(245,158,11,0.5)', 'rgba(239,68,68,0.7)', 'rgba(239,68,68,0.9)'].map((c, i) => (
            <div key={i} style={{ width: 18, height: 10, borderRadius: 2, background: c }} />
          ))}
          <span>High</span>
        </div>
      </div>
    </PanelWrapper>
  );
}
